import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import ProgrammeStats from '../../components/common/ProgrammeStats';
import CompanyEventRow from './CompanyEventRow';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import { LockedCreateButton } from '../../components/common/ModuleInterest';

/* A programme is the theme a run of events hangs under — "Culture year 2027", "Onboarding",
   "Wellbeing". It exists so that twenty townhalls, workshops and family days are not one flat
   list, and so the reporting later has something to group by.

   Deliberately the FIRST screen rather than a flat calendar: an admin who creates events with no
   container ends up with a heap, and no amount of filtering afterwards fixes it. The flat list of
   every event is its own tile on the home, beside this one — not buried behind this screen.

   Each programme carries its next few events (the API sends them), because a row reading "Culture
   year — 12 events" answers how many and never what is next.

   ★ This used to be one screen with five tabs on it — the themes, the values, the goals and the
   houses lived here too, behind a tab strip, sharing one fat read. They are four screens of their
   own now (/themes, /values, /sdgs, /houses), each with its own home tile and its own stats, and
   this one is about the programmes alone. The redirect below is what keeps the old tabbed links
   working. */

const guessZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Brussels';
  } catch {
    return 'Europe/Brussels';
  }
};

const emptyDraft = () => ({ name: '', date: '', end_date: '', short_desc: '',
                            time_zone: guessZone() });

// Where each of the retired tabs went. Anything else in ?tab= is ignored and the programme list
// is what you get — which is also what ?tab=programmes means.
const MOVED_TABS = { themes: '/themes', values: '/values', sdgs: '/sdgs', houses: '/houses' };

/* What a programme is, as four questions: its name, the window it runs, what it is about.

   ONE component for creating one and for changing one, because they ask exactly the same four
   questions — and the editor used to live on the programme's own screen, where a second copy of
   this form drifted a label away from this one. `children` is where the editor hangs the delete
   that creating cannot have. */
const ProgrammeForm = ({ value, onChange, onSave, onCancel, busy, children = null }) => {
  const { t } = useTranslation();
  return (
    <div className="bravos-form">
      <span className="bravos-form__label">{t('programmeName')}</span>
      <input
        type="text"
        value={value.name}
        maxLength={50}
        placeholder={t('programmeNamePlaceholder')}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
      />
      <div className="bravos-form__row">
        <label className="bravos-form__label">
          {t('from')}
          <input
            type="date"
            value={value.date}
            onChange={(e) => onChange({ ...value, date: e.target.value })}
          />
        </label>
        <label className="bravos-form__label">
          {t('to')}
          <input
            type="date"
            value={value.end_date}
            onChange={(e) => onChange({ ...value, end_date: e.target.value })}
          />
        </label>
      </div>
      <textarea
        rows={2}
        maxLength={1024}
        value={value.short_desc}
        placeholder={t('programmeDescPlaceholder')}
        onChange={(e) => onChange({ ...value, short_desc: e.target.value })}
      />
      <div className="bravos-form__row">
        <button
          type="button"
          className="bravos-submit"
          onClick={onSave}
          disabled={busy || !value.name.trim() || !value.date}
        >
          {busy ? t('pleaseWait') : t('save')}
        </button>
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={onCancel}
        >
          {t('cancel')}
        </button>
      </div>
      {children}
    </div>
  );
};

const CompanyProgrammes = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  // The programme being changed — its own fields, with the id of the row they belong to. null =
  // nobody is editing, so the list stays a list. ★ This is where a programme is edited: it used
  // to be a pencil on the programme's own screen, which meant leaving the list to rename a row of
  // it and coming back to find your place again.
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [params] = useSearchParams();
  // Bumped by every write, so the stats under the list re-read themselves rather than leaving
  // yesterday's numbers beside a programme that was just renamed or deleted.
  const [stamp, setStamp] = useState(0);

  /* ★ The old tabbed links — ?tab=values&item=<id> is what /purpose/<kind>/<id> and the home
     badge have been sending for months, and an admin may well have bookmarked a tab. Redirected
     BEFORE the fetch, so a bookmark never flashes the programme list on its way past, and with
     `replace` so Back does not bounce between the two. */
  const movedTo = MOVED_TABS[params.get('tab')];
  useEffect(() => {
    if (!movedTo) return;
    const item = (params.get('item') || '').trim();
    navigate(`${movedTo}${item ? `?item=${encodeURIComponent(item)}` : ''}`, { replace: true });
  }, [movedTo, params, navigate]);

  const load = useCallback(() => {
    apiRequest('GET', '/company/programmes')
      .then((res) => { setData(res); setStamp((n) => n + 1); })
      // Not a contact person, not a Bravos BU, or a company without the engagement product —
      // either way this screen is not for you, and the home is where you belong.
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { if (!movedTo) load(); }, [load, movedTo]);

  const create = () => {
    if (busy || !draft || !draft.name.trim() || !draft.date) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/company/programmes', draft)
      .then((res) => {
        setDraft(null);
        navigate(`/programme/${res.programme.id}`);
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  // ★ The dates come off the API as wall-clock stamps ('2027-01-04T09:00'); a programme is a
  // window, so the form shows the DATE half and sends it back unchanged in the same shape.
  const startEdit = (p) => {
    setNotice(null);
    setDraft(null);
    setEdit({ id: p.id,
              name: p.name || '',
              date: (p.date || '').slice(0, 10),
              end_date: (p.end_date || '').slice(0, 10),
              short_desc: p.short_desc || '' });
  };

  const saveEdit = () => {
    if (busy || !edit || !edit.name.trim() || !edit.date) return;
    setBusy(true);
    setNotice(null);
    const { id, ...body } = edit;
    apiRequest('PUT', `/company/programme/${id}`, body)
      // Reloaded rather than patched in place: the row carries its event count and the next few
      // events under it, and the save answers with the programme alone.
      .then(() => { setEdit(null); load(); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const removeProgramme = () => {
    // eslint-disable-next-line no-alert
    if (busy || !edit || !window.confirm(t('deleteProgrammeConfirm'))) return;
    setBusy(true);
    apiRequest('DELETE', `/company/programme/${edit.id}`)
      .then(() => { setEdit(null); load(); })
      .catch((err) => {
        // The one refusal worth naming: a programme with events under it is not deleted, because
        // that would leave every one of them pointing at nothing. Read off the body rather than
        // off `err.message`, which holds the key only as long as that route sends no prose
        // beside it.
        setNotice((err.data && err.data.error) === 'programmeNotEmpty'
          ? t('deleteProgrammeNotEmpty')
          : apiErrorText(t, err));
      })
      .finally(() => setBusy(false));
  };

  // Nothing is drawn on the way out to a moved tab — a flash of the wrong list is exactly what
  // the redirect exists to prevent.
  if (movedTo) return null;

  const programmes = (data && data.programmes) || [];

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="road" theme="primary" className="me-2" />
          {t('ourProgrammes')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('programmesNote')}</p>

            {programmes.length === 0 && !draft && (
              <p className="ce-note">{t('noProgrammesYet')}</p>
            )}

            {programmes.map((p) => {
              const editing = !!edit && String(edit.id) === String(p.id);
              return (
              <div key={p.id}>
                {/* ★ Editing happens HERE, in place of the row, and the whole row is still the
                    way into the programme — so the chevron came off: with a real button beside
                    it, an arrow that only repeats what the row already does is one target too
                    many on a phone. The edit stops the click reaching the row under it. */}
                {editing ? (
                  <ProgrammeForm
                    value={edit}
                    onChange={setEdit}
                    onSave={saveEdit}
                    onCancel={() => { setEdit(null); setNotice(null); }}
                    busy={busy}
                  >
                    <button
                      type="button"
                      className="bravos-submit bravos-submit--ghost"
                      onClick={removeProgramme}
                      disabled={busy}
                    >
                      <Icon name="trash" /> {t('deleteProgramme')}
                    </button>
                    {notice && <p className="bravos-cal__notice">{notice}</p>}
                  </ProgrammeForm>
                ) : (
                <div
                  className="ce-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/programme/${p.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/programme/${p.id}`); }}
                >
                  <span className="ce-date">
                    <span className="ce-date__day">{p.event_count}</span>
                    <span className="ce-date__mon">{t('eventsShort')}</span>
                  </span>
                  <span className="ce-row__body">
                    <span className="ce-row__name">{p.name}</span>
                    <span className="ce-row__meta">
                      {[shortDate(p.date, i18n.language), shortDate(p.end_date, i18n.language)]
                        .filter(Boolean).join(' – ') || t('themeNoWindow')}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="ce-person__act"
                    title={t('editProgramme')}
                    aria-label={t('editProgramme')}
                    onClick={(ev) => { ev.stopPropagation(); startEdit(p); }}
                  >
                    <Icon name="pencil" />
                  </button>
                </div>
                )}
                {/* What is actually coming up under this programme, without a tap. Rendered as a
                    SIBLING of the programme row and never inside it: a clickable row nested in a
                    clickable row means one of the two taps goes to the wrong screen. */}
                {!editing && (p.upcoming || []).length > 0 && (
                  <div className="ce-sub">
                    {/* ★ When a programme has nothing ahead of it, the server sends the last few
                        events it DID run rather than nothing at all — so the row has to say which
                        of the two it is showing. */}
                    {p.upcoming.every((e) => e.is_past) && (
                      <span className="ce-sub__label">{t('pastEvents')}</span>
                    )}
                    {p.upcoming.map((e) => (
                      <CompanyEventRow
                        key={e.id}
                        event={e}
                        lang={i18n.language}
                        onOpen={() => navigate(`/company-event/${e.id}/edit`)}
                      />
                    ))}
                    {/* Only the next few fit here, so the way to the rest is on the row that
                        stops showing them. Into the programme itself, not the flat calendar:
                        everything under it is there, past included, and that is what "all of
                        them" means from a row that is only showing the next few. */}
                    <button
                      type="button"
                      className="ce-seeall"
                      onClick={() => navigate(`/programme/${p.id}`)}
                    >
                      {t('seeAllEvents', { n: p.event_count })}
                      <Icon name="angle-right" />
                    </button>
                  </div>
                )}
              </div>
              );
            })}

            {draft ? (
              <ProgrammeForm
                value={draft}
                onChange={setDraft}
                onSave={create}
                onCancel={() => setDraft(null)}
                busy={busy}
              />
            ) : (data && data.can_create === false ? (
              /* Seeing programmes is free, making them is the paid "Own events" module. */
              <LockedCreateButton label={t('newProgramme')} />
            ) : (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => { setEdit(null); setDraft(emptyDraft()); }}
              >
                <Icon name="plus" /> {t('newProgramme')}
              </button>
            ))}
            {notice && !edit && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      <Card>
        <Title my="1">{t('progStatsTitle')}</Title>
        <ProgrammeStats key={stamp} />
      </Card>
    </Base>
  );
};

export default CompanyProgrammes;
